/**
 * @file columnFilter.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-26
 * @modified 2026-08-26
 * @description The rules behind the column-header filters, as plain functions.
 *
 *              Separated from the hook so they can be tested without React —
 *              see scripts/check-column-filter.mjs. The interesting decisions
 *              all live here rather than in the state wiring, and they are the
 *              ones that would be quietly wrong:
 *
 *              - no filter and "everything ticked" are the same thing, but
 *                "nothing ticked" is emphatically not — one shows every row,
 *                the other shows none, and collapsing them would make Clear All
 *                a no-op;
 *              - the first tick starts from everything selected, so unticking a
 *                value hides that value. Starting from empty blanks the table on
 *                the first click, which reads as a broken control;
 *              - null, undefined and '' are one option called (blank), because
 *                three ways of saying "nothing here" is three near-identical
 *                rows in the list and no way to filter on any of them.
 */

export const BLANK = '(blank)';

/** What one row contributes to a column's option list. */
export function readValue(definition, row) {
  const raw = definition.value(row);
  if (raw === null || raw === undefined || raw === '') return BLANK;
  return String(raw);
}

/**
 * Distinct values for one column, counted, in the order they appear.
 *
 * Table order rather than alphabetical: the list should read like the column it
 * came from, and the value you just saw on screen should be near the top.
 */
export function optionsFor(rows, definition) {
  const counts = new Map();
  for (const row of rows) {
    const value = readValue(definition, row);
    counts.set(value, (counts.get(value) || 0) + 1);
  }
  return [...counts.entries()].map(([value, count]) => ({ value, count }));
}

/** Rows surviving every active column filter. Columns combine with AND. */
export function applyFilters(rows, definitions, selected) {
  const active = Object.entries(selected).filter(([, set]) => set);
  if (!active.length) return rows;
  return rows.filter((row) => active.every(([key, set]) => {
    const definition = definitions[key];
    if (!definition) return true;
    return set.has(readValue(definition, row));
  }));
}

/**
 * Tick or untick one value.
 *
 * An untouched column starts from everything, so the first click subtracts.
 */
export function toggleValue(rows, definitions, selected, key, value) {
  const base = selected[key]
    || new Set(optionsFor(rows, definitions[key]).map((o) => o.value));
  const next = new Set(base);
  if (next.has(value)) next.delete(value); else next.add(value);
  return { ...selected, [key]: next };
}

/** Everything ticked is the absence of a filter, so the entry is removed. */
export function selectAllValues(selected, key) {
  const next = { ...selected };
  delete next[key];
  return next;
}

/** Nothing ticked is a real state: an empty set, not a missing one. */
export function clearAllValues(selected, key) {
  return { ...selected, [key]: new Set() };
}

/**
 * A cheap fingerprint of a page of rows.
 *
 * Used to decide whether the data actually changed, as opposed to React handing
 * back a fresh array containing the same rows — which several callers do on
 * every render. Length plus the ends is enough: a different page, a different
 * search or a re-sort all move at least one of them, and two genuinely
 * different pages that share length and both ends would have to be sorted
 * identically and differ only in the middle, which a re-fetch of the same query
 * does not produce.
 */
export function rowSignature(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return '0';
  const id = (row) => {
    if (!row || typeof row !== 'object') return String(row);
    return String(row.id ?? row.key ?? row.purchaseOrderID ?? row.invoice_no
      ?? row.order_number ?? row.formulation_id ?? '?');
  };
  return `${rows.length}:${id(rows[0])}:${id(rows[rows.length - 1])}`;
}
